import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LOGO_FULL, NAV_LINKS } from "@/lib/constants";

const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <>
            <header
                data-testid="main-navbar"
                className={`fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-navy-deep/85 backdrop-blur-xl transition-shadow duration-300 ${
                    scrolled ? "shadow-[0_12px_40px_-16px_rgba(1,9,28,0.8)]" : ""
                }`}
            >
                <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 md:px-10">
                    <Link to="/" data-testid="nav-logo" aria-label="Vorantis Soluções — Início" className="flex items-center">
                        <img src={LOGO_FULL} alt="Vorantis Soluções" className="h-10 w-auto" />
                    </Link>
                    <nav className="hidden items-center gap-8 lg:flex" aria-label="Navegação principal">
                        {NAV_LINKS.map((link) => (
                            <NavLink
                                key={link.path}
                                to={link.path}
                                data-testid={link.testid}
                                className={({ isActive }) =>
                                    `group relative py-2 text-sm font-medium transition-colors duration-200 ${
                                        isActive ? "text-white" : "text-slate-300 hover:text-white"
                                    }`
                                }
                            >
                                {({ isActive }) => (
                                    <>
                                        {link.label}
                                        <span
                                            className={`absolute -bottom-0.5 left-0 h-0.5 bg-emerald-400 transition-[width] duration-300 ${
                                                isActive ? "w-full" : "w-0 group-hover:w-full"
                                            }`}
                                        />
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </nav>
                    <div className="flex items-center gap-3">
                        <Link
                            to="/contato"
                            data-testid="nav-cta-diagnostico"
                            className="hidden items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-emerald-600 md:flex"
                        >
                            Solicite um diagnóstico
                            <ArrowUpRight size={16} />
                        </Link>
                        <button
                            data-testid="nav-mobile-toggle"
                            onClick={() => setOpen(!open)}
                            aria-label={open ? "Fechar menu" : "Abrir menu"}
                            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white transition-colors hover:bg-white/10 lg:hidden"
                        >
                            {open ? <X size={20} /> : <Menu size={20} />}
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="nav-mobile-menu"
                        className="fixed inset-0 z-40 flex flex-col justify-center bg-navy-ink px-8 lg:hidden"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <nav className="flex flex-col gap-2" aria-label="Menu móvel">
                            {NAV_LINKS.map((link, i) => (
                                <motion.div
                                    key={link.path}
                                    initial={{ opacity: 0, x: -24 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.08 + i * 0.07, duration: 0.4 }}
                                >
                                    <NavLink
                                        to={link.path}
                                        data-testid={`${link.testid}-mobile`}
                                        className={({ isActive }) =>
                                            `block border-b border-white/10 py-5 font-display text-3xl font-bold tracking-tight ${
                                                isActive ? "text-emerald-400" : "text-white"
                                            }`
                                        }
                                    >
                                        {link.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5, duration: 0.4 }}
                                className="pt-8"
                            >
                                <Link
                                    to="/contato"
                                    data-testid="nav-cta-diagnostico-mobile"
                                    className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-7 py-4 font-semibold text-white"
                                >
                                    Solicite um diagnóstico
                                    <ArrowUpRight size={18} />
                                </Link>
                            </motion.div>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Navbar;
